/**
 * Blinkit-theme shop page (``/restaurant/:id``) — the shop's own catalogue in the same "rail + grid" shape as the
 * category browser. State (shop, categories, products, in-shop search, veg filter) comes from useShopController,
 * the exact logic of the classic page; add-to-cart goes through the shared InlineCartControl on every tile.
 */
import { useState } from "react";
import { Clock, LayoutGrid, MapPin, Search, Star, Store, X } from "lucide-react";
import { getCatalogLabel } from "../../../lib/catalogLabel";
import { computeEtaMinutes, formatDeliveryEta } from "../../../lib/deliveryEta";
import { useCartQuantities } from "../../../lib/useCartQuantities";
import BannerCarouselDynamic from "../../../components/BannerCarouselDynamic";
import BkProductTile from "../../../components/blinkit/BkProductTile";
import { BkChip, BkEmptyState, BkPageHeader, BkTileSkeletonGrid } from "../../../components/blinkit/BkUI";
import { useShopController } from "../controllers/useShopController";

const GRID = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6";

export default function BkShopView() {
    const s = useShopController();
    const { id, tenant, rd, verticals, q, setQ, foodType, setFoodType, showVegFilter, groups } = s;
    const cartMap = useCartQuantities();
    const [sel, setSel] = useState(null);
    const [searching, setSearching] = useState(false);

    if (!rd) {
        return (
            <div className="pb-40" data-testid="bk-shop-page">
                <BkPageHeader title=" " />
                <div className="px-4 pt-4 space-y-4" aria-hidden="true">
                    <div className="h-36 rounded-2xl bg-muted animate-pulse" />
                    <BkTileSkeletonGrid count={4} className={GRID} />
                </div>
            </div>
        );
    }

    const r = rd.restaurant;
    const catalogLabel = getCatalogLabel(r, verticals);
    const eta = formatDeliveryEta(tenant, computeEtaMinutes(r.avg_prep_minutes, r.distance_km));
    const closed = r.open_state === "CLOSED" || r.open_state === "PAUSED";
    // A search / veg filter can empty the selected category — fall back to "All" rather than a blank grid.
    const activeSel = sel && groups.some((g) => g.id === sel) ? sel : null;
    const shown = activeSel ? groups.filter((g) => g.id === activeSel) : groups;
    const hasRail = groups.length > 1;
    const choose = (key) => { setSel(key); window.scrollTo({ top: 0 }); };

    // Same as BkCatalogView: the rail is sticky inside the row below, so the bottom spacing lives on <main> and
    // ``-mb-24`` takes back the shell's bottom-nav padding — otherwise the rail slides up under the header at the end.
    // From md up the rail is shorter by the shell card's bottom margin + border (see BkCatalogView).
    return (
        <div className="-mb-24 min-w-0" data-testid="bk-shop-page">
            <BkPageHeader
                title={r.name}
                subtitle={[eta, typeof r.distance_km === "number" ? `${r.distance_km.toFixed(1)} km` : null].filter(Boolean).join(" · ") || null}
                right={
                    <button type="button" onClick={() => { setSearching((v) => !v); if (searching) setQ(""); }} aria-label={searching ? "Close search" : `Search this ${catalogLabel.toLowerCase()}`}
                            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted" data-testid="bk-shop-search-toggle">
                        {searching ? <X size={20} /> : <Search size={20} />}
                    </button>
                }
            >
                {searching && (
                    <div className="px-4 pb-3">
                        <label className="h-11 flex items-center gap-2 rounded-xl border border-primary bg-card px-3">
                            <Search size={17} className="text-muted-foreground shrink-0" />
                            <input autoFocus data-testid="rest-search" value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Search in ${r.name}`}
                                   placeholder={`Search in ${r.name}`} className="flex-1 min-w-0 bg-transparent outline-none text-[14px]" />
                        </label>
                    </div>
                )}
            </BkPageHeader>

            {/* Shop summary */}
            <section className="px-4 pt-3" data-testid="bk-shop-info">
                <div className="bg-card rounded-2xl border border-border/80 overflow-hidden">
                    {r.cover_image_url && (
                        <div className={`h-28 sm:h-36 bg-muted ${closed ? "grayscale" : ""}`}>
                            <img src={r.cover_image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                    )}
                    <div className="p-4 flex items-start gap-3">
                        <span className="w-12 h-12 shrink-0 rounded-xl bg-primary/10 text-primary overflow-hidden flex items-center justify-center">
                            {r.logo_url ? <img src={r.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={22} />}
                        </span>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-[18px] font-extrabold leading-tight" data-testid="rest-name">{r.name}</h1>
                            {(r.description || (r.cuisines || []).length > 0) && (
                                <p className="text-[12.5px] text-muted-foreground mt-0.5 line-clamp-2">{r.description || (r.cuisines || []).join(" • ")}</p>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-semibold">
                                {r.rating_count > 0 && typeof r.rating === "number" && (
                                    <span className="inline-flex items-center gap-1 rounded-md bg-foreground text-background px-1.5 py-0.5">
                                        {r.rating.toFixed(1)} <Star size={11} className="fill-current" />
                                    </span>
                                )}
                                {eta && <span className="inline-flex items-center gap-1" data-testid="rest-eta"><Clock size={13} className="text-primary" /> {eta}</span>}
                                {typeof r.distance_km === "number" && <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="rest-distance"><MapPin size={13} /> {r.distance_km.toFixed(1)} km away</span>}
                            </div>
                        </div>
                    </div>
                    {closed && (
                        <p className="px-4 py-2.5 bg-destructive/10 text-destructive text-[12.5px] font-bold border-t border-destructive/20">
                            {r.status_label || "This store is closed right now"} — you can browse, ordering opens when it does.
                        </p>
                    )}
                </div>
            </section>

            <BannerCarouselDynamic placement="SHOP" restaurantId={id} className="px-4 mt-3" />

            {showVegFilter && (
                <div className="px-4 mt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="bk-shop-veg">
                    <BkChip active={!foodType} onClick={() => setFoodType(null)} testid="ft-all">All</BkChip>
                    <BkChip active={foodType === "VEG"} onClick={() => setFoodType("VEG")} testid="ft-veg"><span className="food-mark veg" /> Veg</BkChip>
                    <BkChip active={foodType === "NON_VEG"} onClick={() => setFoodType("NON_VEG")} testid="ft-nonveg"><span className="food-mark non-veg" /> Non-veg</BkChip>
                </div>
            )}

            <div className="flex items-start mt-3 border-t border-border">
                {hasRail && (
                    <nav aria-label={`${catalogLabel} categories`} data-testid="bk-shop-rail"
                         className="sticky top-14 self-start shrink-0 w-[76px] sm:w-[96px] lg:w-[116px] max-h-[calc(100dvh-3.5rem)] md:max-h-[calc(100dvh-5rem-1px)] overflow-y-auto no-scrollbar bg-card border-r border-border pb-40">
                        <ShopRailItem active={!activeSel} onClick={() => choose(null)} label="All" count={groups.reduce((n, g) => n + g.items.length, 0)} icon testid="bk-shop-rail-all" />
                        {groups.map((g) => (
                            <ShopRailItem key={g.id} active={activeSel === g.id} onClick={() => choose(g.id)} label={g.name} count={g.items.length}
                                          image={(g.items.find((p) => (p.images || [])[0]) || {}).images?.[0]} testid={`bk-shop-rail-${g.id}`} />
                        ))}
                    </nav>
                )}
                <main className="flex-1 min-w-0 px-3 sm:px-4 pt-3 pb-64" data-testid="cat-sections">
                    {groups.length === 0 ? (
                        <BkEmptyState icon={LayoutGrid} testid="bk-shop-empty"
                                      title={q || foodType ? "No items match" : `${catalogLabel} coming soon`}
                                      body={q || foodType ? "Try a different word or clear the filters." : "This store hasn't added products yet."}
                                      action={(q || foodType) && <button type="button" onClick={() => { setQ(""); setFoodType(null); }} className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">Clear filters</button>} />
                    ) : shown.map((g) => (
                        <section key={g.id} className="mb-6" data-testid={`cat-section-${g.id}`}>
                            <div className="flex items-baseline justify-between gap-2 mb-3">
                                <h2 className="text-[16px] font-extrabold leading-tight truncate">{g.name}</h2>
                                <span className="text-[12px] text-muted-foreground shrink-0 tabular-nums">{g.items.length} item{g.items.length === 1 ? "" : "s"}</span>
                            </div>
                            <div className={`grid gap-2.5 sm:gap-3 ${hasRail ? GRID : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-7"}`}>
                                {g.items.map((p) => <BkProductTile key={p.id} product={{ ...p, restaurant_id: p.restaurant_id || id }} qty={cartMap[p.id] || 0} showShop={false} />)}
                            </div>
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );
}

function ShopRailItem({ active, onClick, label, count, image, icon, testid }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid} aria-current={active ? "true" : undefined}
                className={`relative w-full flex flex-col items-center gap-1.5 px-1.5 py-3 text-center transition ${active ? "bg-primary/[0.08] text-primary" : "hover:bg-muted/60"}`}>
            <span className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl overflow-hidden flex items-center justify-center text-primary ${active ? "bg-card ring-2 ring-primary/30" : "bg-primary/[0.06]"}`}>
                {image ? <img src={image} alt="" className="w-full h-full object-cover" loading="lazy" /> : icon ? <LayoutGrid size={22} /> : <Store size={20} />}
            </span>
            <span className={`text-[11px] sm:text-[12px] leading-tight line-clamp-2 break-words max-w-full ${active ? "font-extrabold" : "font-medium"}`}>{label}</span>
            <span className="text-[10px] text-muted-foreground tabular-nums">{count}</span>
            {active && <span className="absolute right-0 top-2 bottom-2 w-[3px] rounded-l-full bg-primary" aria-hidden="true" />}
        </button>
    );
}
